import { useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { API, formatDetail } from "./bits";

export const LoginCard = ({ onLoggedIn }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login");
  const [forgotSent, setForgotSent] = useState(false);

  const onForgot = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    setSubmitting(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      setForgotSent(true);
    } catch {
      setForgotSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatDetail(body.detail));
      onLoggedIn(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-card" className="card-dark mx-auto max-w-md p-8 sm:p-10 hover:transform-none">
      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#1E3A8A]/40 bg-[#1E3A8A]/10">
        <Lock size={20} className="text-[#1E3A8A]" />
      </span>
      <h1 className="mt-6 font-sans text-2xl text-[#0F172A] sm:text-3xl">Admin Dashboard</h1>
      <p className="mt-2 text-sm text-[#94A3B8]">Private area — Aflimart admin only.</p>

      {mode === "forgot" ? (
        forgotSent ? (
          <div className="mt-8" data-testid="forgot-sent">
            <p className="rounded-lg border border-[#1E3A8A]/30 bg-[#1E3A8A]/5 px-4 py-3 text-sm leading-relaxed text-[#475569]">
              If that email is registered, a reset link is on its way. The link expires in 30 minutes.
            </p>
            <button
              type="button"
              onClick={() => { setMode("login"); setForgotSent(false); }}
              data-testid="forgot-back-login"
              className="mt-6 font-sans text-[11px] tracking-[0.2em] text-[#1E3A8A]/80 underline-offset-4 transition-colors hover:text-[#1E3A8A] hover:underline"
            >
              BACK TO SIGN IN
            </button>
          </div>
        ) : (
          <form onSubmit={onForgot} className="mt-8 space-y-7" data-testid="forgot-password-form">
            <div>
              <label className="field-label" htmlFor="forgot-email">Admin email <span className="text-[#1E3A8A]">*</span></label>
              <input id="forgot-email" name="email" type="email" required data-testid="forgot-email-input" className="field" placeholder="you@example.com" autoComplete="username" />
            </div>
            <button type="submit" disabled={submitting} data-testid="forgot-submit-button" className="btn-gold w-full disabled:opacity-60 disabled:hover:translate-y-0">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : "Email Me a Reset Link"}
            </button>
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="forgot-cancel-button"
              className="w-full text-center font-sans text-[11px] tracking-[0.2em] text-[#94A3B8] underline-offset-4 transition-colors hover:text-[#475569] hover:underline"
            >
              BACK TO SIGN IN
            </button>
          </form>
        )
      ) : (
      <form onSubmit={onSubmit} className="mt-8 space-y-7" data-testid="admin-login-form">
        <div>
          <label className="field-label" htmlFor="admin-email">Email <span className="text-[#1E3A8A]">*</span></label>
          <input id="admin-email" name="email" type="email" required data-testid="admin-email-input" className="field" placeholder="you@example.com" autoComplete="username" />
        </div>
        <div>
          <label className="field-label" htmlFor="admin-password">Password <span className="text-[#1E3A8A]">*</span></label>
          <input id="admin-password" name="password" type="password" required data-testid="admin-password-input" className="field" placeholder="••••••••" autoComplete="current-password" />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="rounded-lg border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
        )}
        <button type="submit" disabled={submitting} data-testid="admin-login-button" className="btn-gold w-full disabled:opacity-60 disabled:hover:translate-y-0">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </button>
        <button
          type="button"
          onClick={() => { setMode("forgot"); setError(""); }}
          data-testid="forgot-password-link"
          className="w-full text-center font-sans text-[11px] tracking-[0.2em] text-[#94A3B8] underline-offset-4 transition-colors hover:text-[#1E3A8A] hover:underline"
        >
          FORGOT PASSWORD?
        </button>
      </form>
      )}
    </div>
  );
};
